"use client";
import { useFormContext } from "react-hook-form";
import { Card, CardContent } from "~/components/v3/shadcn/Card";
import { Label, SubLabel } from "~/components/v3/shadcn/Label";
import { Input } from "~/components/v3/themed/Input";
import { TextArea } from "~/components/v3/themed/TextArea";
import { ApplyToSponsorFormData } from "./types";
import { BiLogoInstagram, BiLogoLinkedin } from "react-icons/bi";
import { FaFacebookSquare, FaTwitter } from "react-icons/fa";
import { useParams } from "next/navigation";
import UploadImage from "~/components/file/UploadImage";
import GenericImage from "~/components/image-components/GenericImage";
import { URL_REGEX } from "~/utils/constants";
import { sanitizeEmail } from "~/utils/formatters";
import { cn } from "~/utils/fontsConfig";
import { If } from "~/components/v3/Conditional";

export const SponsorStep2 = () => {
    const {
        register,
        formState: { errors },
        setValue,
        watch,
        getValues,
    } = useFormContext<ApplyToSponsorFormData>();

    const params = useParams();
    const eventSlug = params?.slug;

    const handleImageUpload = (filename: string) => {
        setValue("image", filename, { shouldValidate: true });
    };

    return (
        <div
            className={cn(
                "flex h-full w-full flex-1 flex-grow flex-col bg-black p-2 text-white",
                "gap-2",
            )}
        >
            <Card className="w-full">
                <CardContent className="w-full p-4">
                    <div>
                        <Label htmlFor="name" className="text-white">
                            Ad name
                        </Label>
                        <Input
                            id="name"
                            {...register("name", {
                                required: "Ad name is required",
                            })}
                            placeholder="Sponsor Name"
                            className="mt-1 w-full"
                        />
                        {errors.name && (
                            <span className="text-red-500">
                                {errors.name.message}
                            </span>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="email" className="text-white">
                            Email
                        </Label>
                        <Input
                            id="email"
                            type="email"
                            {...register("email", {
                                required: "Email is required",
                                setValueAs: (value: string) =>
                                    sanitizeEmail(value),
                                pattern: {
                                    value: /\S+@\S+\.\S+/,
                                    message: "Invalid email address",
                                },
                            })}
                            placeholder="Enter your email address"
                            className="mt-1 w-full text-white"
                        />
                        {errors.email && (
                            <span className="text-red-500">
                                {errors.email.message}
                            </span>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="description" className="text-white">
                            Description
                        </Label>
                        <TextArea
                            className="mt-1 min-h-[150px] w-full text-white"
                            id="description"
                            {...register("description", {
                                required: "Description is required",
                            })}
                            rows={4}
                            placeholder="A short description of your company or offer"
                        />
                        {errors.description && (
                            <span className="text-red-500">
                                {errors.description.message}
                            </span>
                        )}
                    </div>

                    {(getValues("selectedPackageTier").priceType ===
                        "CUSTOM_OPEN" ||
                        getValues("selectedPackageTier").priceType ===
                            "CUSTOM_MINIMUM") && (
                        <div>
                            <Label htmlFor="customPrice" className="text-white">
                                Amount you want to sponsor
                            </Label>
                            <div className="flex w-full items-center gap-2 md:w-[50%]">
                                <Input
                                    id="customPriceDollars"
                                    {...register("customPriceDollars", {
                                        required: "Dollar amount is required",
                                        min: {
                                            value: 0,
                                            message: "Amount must be positive",
                                        },
                                    })}
                                    placeholder="Dollars"
                                    className="mt-1 w-full text-white"
                                />
                                <Input
                                    id="customPriceCents"
                                    {...register("customPriceCents", {
                                        required: "Cents amount is required",
                                        min: {
                                            value: 0,
                                            message: "Amount must be positive",
                                        },
                                        max: {
                                            value: 99,
                                            message:
                                                "Cents must be between 0 and 99",
                                        },
                                    })}
                                    placeholder="Cents"
                                    className="mt-1 w-full text-white"
                                />
                            </div>
                            {errors.customPriceDollars && (
                                <span className="mt-1 block text-red-500">
                                    {errors.customPriceDollars.message}
                                </span>
                            )}
                            {errors.customPriceCents && (
                                <span className="mt-1 block text-red-500">
                                    {errors.customPriceCents.message}
                                </span>
                            )}
                        </div>
                    )}

                    {getValues("selectedPackageTier").durationType ===
                        "CUSTOM" && (
                        <div>
                            <Label
                                htmlFor="customDuration"
                                className="text-white"
                            >
                                Duration (number of months)
                            </Label>
                            <Input
                                id="customDuration"
                                {...register("customDuration", {
                                    required: "Duration is required",
                                    min: {
                                        value: 1,
                                        message:
                                            "Duration must be at least 1 month",
                                    },
                                })}
                                placeholder="Duration in months"
                                className="mt-1 w-full text-white"
                            />
                            {errors.customDuration && (
                                <span className="text-red-500">
                                    {errors.customDuration.message}
                                </span>
                            )}
                        </div>
                    )}

                    <div>
                        <Label htmlFor="additionalInfo" className="text-white">
                            Additional Info
                        </Label>
                        <TextArea
                            className="mt-1 min-h-[100px] w-full text-white"
                            id="additionalInfo"
                            {...register("additionalInfo")}
                            rows={2}
                            placeholder="Any additional info you'd like to share about this sponsorship"
                        />
                        {errors.description && (
                            <span className="text-red-500">
                                {errors.description.message}
                            </span>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="url" className="text-white">
                            Call to Action URL
                        </Label>
                        <Input
                            id="url"
                            type="url"
                            {...register("url", {
                                required: "URL is required",
                                pattern: {
                                    value: URL_REGEX,
                                    message: "Invalid URL",
                                },
                            })}
                            placeholder="Where you would like people to click"
                            className="mt-1 w-full text-white"
                        />
                        {errors.url && (
                            <span className="text-red-500">
                                {errors.url.message}
                            </span>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="image" className="mr-2 text-white">
                            Sponsor Image
                        </Label>
                        <SubLabel>Brand logo or headshot</SubLabel>
                        <div className="mt-2">
                            <UploadImage
                                bucketKey={`events/${eventSlug}/sponsors`}
                                onUploadComplete={handleImageUpload}
                                variant="button"
                                className="w-full"
                            />
                        </div>
                        {watch("image") && (
                            <GenericImage
                                filename={watch("image") as string}
                                imageSrcTemplate={(filename) =>
                                    `${process.env.NEXT_PUBLIC_ASSETS_URL}/events/${eventSlug}/sponsors/${filename}`
                                }
                            />
                        )}
                    </div>

                    <Label className="text-white">Social Links</Label>
                    <div className="flex w-full items-center gap-2">
                        <FaTwitter className="h-6 w-6 text-[#1DA1F2]" />
                        <Input
                            placeholder="Twitter/X handle..."
                            prefixComponent={<span>@</span>}
                            className="grow bg-transparent"
                            {...register("socialLinks.x")}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <BiLogoLinkedin className="h-6 w-6 text-[#0A66C2]" />
                        <Input
                            placeholder="LinkedIn profile URL"
                            className="flex-1 bg-transparent"
                            {...register("socialLinks.li")}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <BiLogoInstagram className="h-6 w-6 text-[#E4405F]" />
                        <Input
                            placeholder="Instagram profile URL"
                            className="flex-1 bg-transparent"
                            {...register("socialLinks.ig")}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <FaFacebookSquare className="h-6 w-6 text-[#1877F2]" />
                        <Input
                            placeholder="Facebook profile URL"
                            className="flex-1 bg-transparent"
                            {...register("socialLinks.fb")}
                        />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
